/**
 * Ask for a reset link.
 *
 * ONE OUTCOME, WHATEVER THE SERVER SAYS. `POST /auth/forgot-password` answers 202
 * for an address that exists and 202 for one that does not, on purpose: a form
 * that answered differently would be a free tool for working out who has an
 * account here, and this panel's user list is the list of people with access to
 * call recordings. So there is no "no such account" branch to write, and adding
 * one later would quietly undo the endpoint's whole design.
 *
 * WHICH MEANS THE CONFIRMATION MUST NOT CLAIM AN EMAIL WAS SENT. It says "if
 * there is an account" and then says why it is being cagey, in one sentence, on
 * screen. The alternative — "check your inbox" — is a sentence that is false half
 * the time and sends the other half of those people hunting through spam folders
 * for a message that was never generated. Being honest about the ambiguity costs
 * a line and saves the support ticket.
 *
 * A TRANSPORT FAILURE IS STILL AN ERROR, and this is the one place the rule above
 * stops. "Regardless of the response" is about what the server said; if the
 * request never reached a server there is no response to be discreet about, and
 * showing the confirmation would be claiming something nothing could have done.
 * Any answer with a status — including a 429 — gets the confirmation, because
 * status codes are the server's answer and revealing which ones came back is
 * exactly the leak the endpoint exists to prevent.
 */
import { useState } from 'react'
import { ApiError } from '@/api'
import { auth } from '@/lib/auth'
import { AuthShell } from '@/components/auth-shell'
import { Link } from '@/components/app-sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [asked, setAsked] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    auth
      .forgotPassword(email, AbortSignal.timeout(20_000))
      .then(() => setAsked(true))
      .catch((err: unknown) => {
        setBusy(false)
        // An ApiError means a server answered. Which status it answered with is
        // its business and not this screen's to relay.
        if (err instanceof ApiError) setAsked(true)
        else setError(err instanceof Error ? err.message : String(err))
      })
  }

  if (asked) {
    return (
      <AuthShell
        title="Check your email"
        footer={
          <Link to="/login" className="underline underline-offset-4">
            Back to sign in
          </Link>
        }
      >
        <div className="flex flex-col gap-3 text-sm">
          <p>
            If <span className="mono">{email}</span> has an account here, a reset link is on
            its way to it.
          </p>
          <p className="text-muted-foreground">
            We do not say whether it does. Answering that question on this screen would let
            anybody check who has access to this panel, so the reply is the same either way
            — which does mean that no email is sent if the address is not one of ours.
          </p>
          <p className="text-muted-foreground">
            The link expires. If it has, come back here and ask for another.
          </p>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Forgot your password"
      intro="Give us the address you sign in with and we will send a link to set a new password."
      footer={
        <Link to="/login" className="underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="field gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            autoFocus
          />
        </div>

        {error && <p className="err text-sm">{error}</p>}

        <Button type="submit" disabled={busy || email === ''}>
          {busy ? 'Sending…' : 'Send the reset link'}
        </Button>
      </form>
    </AuthShell>
  )
}
